import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AnalyticsRouteTracker } from '@/analytics/AnalyticsRouteTracker';
import { AppShell } from '@/components/layout/AppShell';
import { AdminAccessGate } from '@/components/layout/AdminAccessGate';
import { LazyPage } from '@/routes/LazyPage';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { AuthPage } from '@/pages/AuthPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Route-level code splitting: each page (calendar, Terminal checkout, payroll,
// etc.) loads on demand instead of shipping in one admin bundle.
const AppDashboard = lazy(() =>
  import('@/pages/AppDashboard').then((module) => ({ default: module.AppDashboard })),
);
const ClientsPage = lazy(() =>
  import('@/pages/ClientsPage').then((module) => ({ default: module.ClientsPage })),
);
const GiftCardsPage = lazy(() =>
  import('@/pages/GiftCardsPage').then((module) => ({ default: module.GiftCardsPage })),
);
const ProductsPage = lazy(() =>
  import('@/pages/ProductsPage').then((module) => ({ default: module.ProductsPage })),
);
const ReportsPage = lazy(() =>
  import('@/pages/ReportsPage').then((module) => ({ default: module.ReportsPage })),
);
const RevenuePage = lazy(() =>
  import('@/pages/RevenuePage').then((module) => ({ default: module.RevenuePage })),
);
const ServicesPage = lazy(() =>
  import('@/pages/ServicesPage').then((module) => ({ default: module.ServicesPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);
const PayrollPage = lazy(() =>
  import('@/pages/PayrollPage').then((module) => ({ default: module.PayrollPage })),
);
const StaffPage = lazy(() =>
  import('@/pages/StaffPage').then((module) => ({ default: module.StaffPage })),
);
const TimeClockKioskPage = lazy(() =>
  import('@/pages/TimeClockKioskPage').then((module) => ({
    default: module.TimeClockKioskPage,
  })),
);
const CashDrawerPage = lazy(() =>
  import('@/pages/CashDrawerPage').then((module) => ({
    default: module.CashDrawerPage,
  })),
);

export const router = createBrowserRouter([
  {
    element: <AnalyticsRouteTracker />,
    children: [
      {
        element: (
          <ProtectedRoute>
            <AdminAccessGate>
              <AppShell />
            </AdminAccessGate>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <LazyPage>
                <AppDashboard />
              </LazyPage>
            ),
          },
          {
            path: 'sales',
            element: (
              <LazyPage>
                <RevenuePage />
              </LazyPage>
            ),
          },
          {
            path: 'gift-cards',
            element: (
              <LazyPage>
                <GiftCardsPage />
              </LazyPage>
            ),
          },
          {
            path: 'clients',
            element: (
              <LazyPage>
                <ClientsPage />
              </LazyPage>
            ),
          },
          {
            path: 'services',
            element: (
              <LazyPage>
                <ServicesPage />
              </LazyPage>
            ),
          },
          {
            path: 'products',
            element: (
              <LazyPage>
                <ProductsPage />
              </LazyPage>
            ),
          },
          {
            path: 'settings',
            element: (
              <LazyPage>
                <SettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'reports',
            element: (
              <LazyPage>
                <ReportsPage />
              </LazyPage>
            ),
          },
          {
            path: 'staff',
            element: (
              <LazyPage>
                <StaffPage />
              </LazyPage>
            ),
          },
          {
            path: 'payroll',
            element: (
              <LazyPage>
                <PayrollPage />
              </LazyPage>
            ),
          },
          {
            path: 'clock',
            element: (
              <LazyPage>
                <TimeClockKioskPage />
              </LazyPage>
            ),
          },
          {
            path: 'cash-drawer',
            element: (
              <LazyPage>
                <CashDrawerPage />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: '/auth',
        element: <AuthPage />,
      },
      {
        path: '/login',
        element: <Navigate to="/auth" replace />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
